"use client";

import Pagination from "@/components/pagination";
import { Badge } from "@/components/shadcn/badge";
import { Button } from "@/components/shadcn/button";
import { useSession } from "@/lib/auth/client";
import { fetcher } from "@/lib/fetcher";
import {
    LEADERBOARD_PAGE_SIZE,
    leaderboardEndpoint,
    type LeaderboardBoard,
    type LeaderboardPage,
    type LeaderboardRow,
} from "@/lib/leaderboard/boards";
import { cn } from "cn";
import { useTranslations } from "next-intl";
import { useState } from "react";
import useSWR from "swr";

function LeaderboardItem({
    row,
    isCurrent,
}: {
    row: LeaderboardRow;
    isCurrent: boolean;
}) {
    return (
        <li
            aria-current={isCurrent || undefined}
            className={cn(
                "flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm",
                isCurrent && "bg-primary text-primary-foreground font-medium",
            )}
        >
            <span className="truncate">
                {row.rank}. {row.name}
            </span>
            <Badge
                variant="ghost"
                className={cn(
                    "text-primary rounded-md tabular-nums",
                    isCurrent ? "bg-primary-foreground" : "bg-primary/10",
                )}
            >
                {row.score}
            </Badge>
        </li>
    );
}

function LeaderboardError({ onRetry }: { onRetry: () => void }) {
    const t = useTranslations();
    return (
        <div className="text-muted-foreground flex flex-col items-center gap-2 py-4 text-center text-sm">
            {t("ui.leaderboardError")}
            <Button variant="outline" size="sm" onClick={onRetry}>
                {t("common.retry")}
            </Button>
        </div>
    );
}

function LeaderboardSkeleton() {
    return (
        <ul className="flex flex-col gap-0.5" aria-hidden="true">
            {Array.from({ length: LEADERBOARD_PAGE_SIZE }, (_, index) => (
                <li key={index} className="py-1.5">
                    <div className="bg-muted h-5 w-full animate-pulse rounded-md" />
                </li>
            ))}
        </ul>
    );
}

/**
 * A leaderboard fetched a page at a time, with loading, error and empty
 * states. The signed-in viewer's row is highlighted in place, or pinned below
 * the page when it falls on another one. Switching `board` (another board or
 * filter) starts again from the first page.
 */
export function Leaderboard({ board }: { board: LeaderboardBoard }) {
    const endpoint = leaderboardEndpoint(board);
    return <LeaderboardPages key={endpoint} endpoint={endpoint} />;
}

function LeaderboardPages({ endpoint }: { endpoint: string }) {
    const t = useTranslations("ui");
    const [page, setPage] = useState(1);
    const { data: session } = useSession();

    // The previous page stays up while the next one loads.
    const { data, error, isLoading, mutate } = useSWR<LeaderboardPage>(
        `${endpoint}?page=${page}`,
        fetcher,
        { keepPreviousData: true },
    );
    const { data: viewerRow } = useSWR<LeaderboardRow | null>(
        session ? `${endpoint}/me` : null,
        fetcher,
    );
    const retry = () => void mutate();

    // The board can shrink between reads, and each server keeps its own copy
    // of it, so a page past the end moves back to the last one.
    if (data && data.pageCount > 0 && page > data.pageCount) {
        setPage(data.pageCount);
    }

    if (!data) {
        return error ? (
            <LeaderboardError onRetry={retry} />
        ) : (
            <LeaderboardSkeleton />
        );
    }

    if (data.pageCount === 0) {
        return (
            <p className="text-muted-foreground py-8 text-center text-sm">
                {t("leaderboardEmpty")}
            </p>
        );
    }

    // A page past the end, while the last page loads.
    if (data.rows.length === 0) return <LeaderboardSkeleton />;

    // The viewer's row, when it is not on the page shown.
    const pinnedRow =
        viewerRow && !data.rows.some((row) => row.userId === viewerRow.userId)
            ? viewerRow
            : undefined;

    return (
        <div className="flex flex-1 flex-col gap-4">
            <ol
                aria-busy={isLoading}
                className={cn(
                    "flex flex-col gap-0.5 transition-opacity",
                    isLoading && "opacity-60",
                )}
            >
                {data.rows.map((row) => (
                    <LeaderboardItem
                        key={row.userId}
                        row={row}
                        isCurrent={row.userId === viewerRow?.userId}
                    />
                ))}
                {pinnedRow && (
                    <>
                        <li
                            aria-hidden
                            className="border-muted-foreground/50 ml-3 h-3 w-px border-l border-dotted"
                        />
                        <LeaderboardItem row={pinnedRow} isCurrent />
                    </>
                )}
            </ol>
            {/* A failed page change keeps the last good page up. */}
            {error && <LeaderboardError onRetry={retry} />}
            {data.pageCount > 1 && (
                <Pagination
                    className="mt-auto"
                    pageCount={data.pageCount}
                    currentPage={page}
                    onPageChange={setPage}
                    // ~300px columns: five page slots and no first/last chevrons.
                    siblingCount={0}
                    showFirstLast={false}
                />
            )}
        </div>
    );
}
